import { useState } from "react";
import { Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import StudentPill from "./student-pill";
import {
  UnderstandingLevel,
  type GroupedStudent,
  type TranscriptMessageValues,
} from "@live-writing/models";
import { getUnderstandingColor } from "@/lib/understanding-utils";
import ApiClient from "@/lib/api-client";
import TranscriptModal from "./transcript-modal";

export default function UnderstandingSection({
  group,
}: {
  group: {
    level: UnderstandingLevel;
    label: string;
    students: GroupedStudent[];
    teachingTips?: string[];
  };
}) {
  const [showAdditionalTips, setShowAdditionalTips] = useState(false);
  const [openTranscriptFor, setOpenTranscriptFor] =
    useState<GroupedStudent | null>(null);
  const [transcript, setTranscript] = useState<
    TranscriptMessageValues[]
  >([]);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [transcriptError, setTranscriptError] = useState<string | null>(null);

  const openTranscript = async (student: GroupedStudent) => {
    setOpenTranscriptFor(student);
    setTranscript([]);
    setTranscriptError(null);
    setLoadingTranscript(true);
    try {
      const res = await ApiClient.get<{
        student_submission: { transcript: TranscriptMessageValues[] };
      }>(`/student-submission-transcript/${student.id}`);
      setTranscript(res.student_submission?.transcript || []);
    } catch (err) {
      console.error("Failed to fetch transcript:", err);
      setTranscriptError("Failed to load transcript");
    } finally {
      setLoadingTranscript(false);
    }
  };

  return (
    <div className="rounded-lg mb-6 overflow-hidden border border-gray-200 w-full">
      <div
        className="px-6 py-2"
        style={{ backgroundColor: getUnderstandingColor(group.level) }}
      >
        <h3 className="text-[16px] font-semibold text-white">{group.label}</h3>
      </div>

      <div className="p-6 bg-white">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          {group.students.map((student) => (
            <div key={student.id} className="mr-3 mb-2">
              <StudentPill
                student={student}
                onClick={() => openTranscript(student)}
              />
            </div>
          ))}
        </div>

        {group.teachingTips && (
          <div
            className="mt-4 rounded p-4 border"
            style={{
              backgroundColor: `${getUnderstandingColor(group.level)}20`,
              borderColor: `${getUnderstandingColor(group.level)}40`,
            }}
          >
            <div className="flex items-start gap-3">
              <Lightbulb className="h-4 w-4 text-muted-foreground mt-1" />
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-2">
                  {group.level === UnderstandingLevel.INCOMPLETE
                    ? "No teaching tips available"
                    : "Teaching Tips"}
                </div>
                <ul className="space-y-2">
                  {group.teachingTips.slice(0, 3).map((tip, index) => (
                    <li
                      key={index}
                      className="text-sm text-muted-foreground flex items-start gap-2"
                    >
                      <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full mt-2 shrink-0" />
                      {tip}
                    </li>
                  ))}

                  {showAdditionalTips && group.teachingTips.length > 3 && (
                    <>
                      {group.teachingTips.slice(3).map((tip, index) => (
                        <li
                          key={index + 3}
                          className="text-sm text-muted-foreground flex items-start gap-2"
                        >
                          <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full mt-2 shrink-0" />
                          {tip}
                        </li>
                      ))}
                    </>
                  )}
                </ul>
              </div>
            </div>

            {group.teachingTips.length > 3 && (
              <div className="mt-4 flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAdditionalTips(!showAdditionalTips)}
                  className="text-xs"
                >
                  {showAdditionalTips ? "Show Less" : "Additional Tips"}
                </Button>
              </div>
            )}
          </div>
        )}

        <TranscriptModal
          open={!!openTranscriptFor}
          onClose={() => setOpenTranscriptFor(null)}
          student={openTranscriptFor}
          transcript={transcript}
          loading={loadingTranscript}
          error={transcriptError}
        />
      </div>
    </div>
  );
}
